/* Visuelle element i tilbodet:
   – RigBand: riggen som blir bygd modul for modul (R3F, `presentasjon/rigHero`). Lasta lat og montert først når bandet
     nærmar seg biletet (byggjinga startar då – og startar på nytt om ein rullar langt vekk og tilbake). RigHero viser
     sjølv plakat ved redusert rørsle / utan WebGL.
   – DataFlow: illustrasjonen «samla data → betre KI-støtte». Tre steg: der informasjonen finst i dag → relevante data
     kopla saman (ringen frå presentasjonen, tilpassa: «felles datagrunnlag – ikkje nødvendigvis eitt system») → kva KI
     då kan gjere, med folk som stadfestar (og avgjerdene som går attende i grunnlaget). HTML for all tekst (alltid
     synleg, responsiv); SVG for ringen og for strekane som blir målte frå elementa (≥1280 px). Rørsle, medvite enkel:
     ringen og strekane teiknar seg éin gong når illustrasjonen nærmar seg, innhaldet tonar inn – ingenting rører seg
     kontinuerleg, og ingenting ved redusert rørsle.
   – InitiativeScene: «Utgangspunktet» som ei fast (sticky), rulledriven scene i tre takter – initiativa som alt er i gang
     blir tende, driv frå kvarandre, og legg seg til slutt på éi felles linje. Sjå kommentaren ved komponenten. */
import { Suspense, forwardRef, lazy, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { LottieSvg } from "lottie-react";
import { ArrowRight, BookOpen, Database, FlaskConical, Layers, Mail, PenTool, Smartphone, Table2, UserCheck, Users, Workflow } from "lucide-react";
import "@/presentasjon/pres.css";

const RigHero = lazy(() => import("@/presentasjon/rigHero").then((m) => ({ default: m.RigHero })));

/* synleg (med margin) → true; utanfor → false. */
const useVisible = (ref, margin = "240px") => {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setOn(true);
      return undefined;
    }
    const io = new IntersectionObserver((entries) => setOn(entries[entries.length - 1].isIntersecting), { rootMargin: `${margin} 0px` });
    io.observe(el);
    return () => io.disconnect();
  }, [ref, margin]);
  return on;
};

const useMedia = (query) => {
  const [m, setM] = useState(() => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia(query).matches : false));
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const fn = () => setM(mq.matches);
    fn();
    mq.addEventListener?.("change", fn);
    return () => mq.removeEventListener?.("change", fn);
  }, [query]);
  return m;
};

/* ---------------- Riggen ---------------- */
export const RigBand = ({ eyebrow, title, text, testId = "offer-rig" }) => {
  const ref = useRef(null);
  const near = useVisible(ref, "45%");
  const far = useVisible(ref, "900px");
  /* klokka startar alt når bandet er ~30 % av skjermhøgda unna, og går i raskare tempo – så byggjinga er godt i gang idet
     ein ser bandet, og ein rullar ikkje forbi utan å få det med seg */
  const inView = useVisible(ref, "30%");
  const [mounted, setMounted] = useState(false);
  /* montert når bandet nærmar seg, avmontert først når det er langt unna (byggjinga startar så på nytt);
     renderløkka går berre medan bandet er i (eller like under) biletet */
  useEffect(() => {
    if (near) setMounted(true);
    else if (!far) setMounted(false);
  }, [near, far]);
  return (
    <figure ref={ref} className="offer-rig" data-testid={testId} data-mounted={mounted ? "1" : "0"} data-active={inView ? "1" : "0"}>
      <div className="offer-rig-grid" aria-hidden="true" />
      <div className="offer-rig-stage">
        {mounted && (
          <Suspense fallback={null}>
            <RigHero className="offer-rig-hero" testid={`${testId}-visual`} active={inView} speed={1.45} startAt={0.5} />
          </Suspense>
        )}
      </div>
      <figcaption className="offer-rig-caption">
        <span className="offer-eyebrow">{eyebrow}</span>
        <p className="offer-rig-title">{title}</p>
        {text && <p className="offer-rig-text">{text}</p>}
      </figcaption>
    </figure>
  );
};


/* ---------------- Samla data → betre KI-støtte ---------------- */
const SOURCES = [
  { icon: Database, label: "ERP", sub: "ordre, prosjekt og fakturering" },
  { icon: BookOpen, label: "Sana", sub: "kunnskap og opplæring" },
  { icon: PenTool, label: "Miro", sub: "arbeidsflater og prosesskart" },
  { icon: Mail, label: "E-post og dokument", sub: "tilbod, avtalar og teikningar" },
  { icon: Table2, label: "Rekneark", sub: "kalkylar, lister og oversikter" },
  { icon: Smartphone, label: "Felt", sub: "sjekklister, bilete og avvik" },
];
/* åtte fagområde rundt ringen – same rekkjefølgje som i presentasjonen, start på toppen og med klokka */
const DOMAINS = ["Kundar og tilbod", "Riggdesign", "Kapasitet og flåte", "Transport og montasje", "Innkvartering og drift", "Økonomi", "Felt og sjekklister", "Dokument og søknader"];
const KI = [
  { v: "svare", t: "«Kan vi levere 120 personar på Mongstad frå 1. desember?» Svar med kjelder frå systema de allereie har." },
  { v: "føreslå", t: "Sourcingplan, riggdesign og tilbodstekst – alltid med grunngjeving, slik at ein ser kvifor." },
  { v: "handle", t: "Reservere, opprette, varsle – berre som forslag, aldri på eiga hand." },
];

/* ring-geometri (SVG-koordinatar; etikettane er HTML plasserte i prosent, så dei held skarp pikselstorleik) */
const S = 600, C = 300, R = 212, CORE_R = 60;
const NODES = DOMAINS.map((label, i) => {
  const a = -Math.PI / 2 + (i * Math.PI * 2) / DOMAINS.length;
  const cos = Math.cos(a), sin = Math.sin(a);
  /* etikettside: venstre/høgre node får etiketten under seg (der kjem/går strekane), elles ut frå ringen */
  const side = Math.abs(cos) > 0.95 ? "bottom" : cos > 0.3 ? "right" : cos < -0.3 ? "left" : sin < 0 ? "top" : "bottom";
  return { label, i, cos, sin, x: C + R * cos, y: C + R * sin, side };
});
/* kvar strekane frå kjeldene kjem inn (venstre node) og kvar hovudstreken går ut (høgre node) */
const PORT_IN = { x: (C - R) / S, y: 0.5 };
const PORT_OUT = { x: (C + R) / S, y: 0.5 };

/* S-kurve med horisontale tangentar. `a`/`b` (0–1) styrer kvar den vertikale rørsla skjer: tidleg (inn mot ringen, så
   strekane går flatt inn mot noden og ikkje kryssar etikettane) eller seint (ut frå ringen). */
const curve = (x1, y1, x2, y2, a = 0.5, b = 0.5) => {
  const dx = x2 - x1;
  return `M${x1.toFixed(1)} ${y1.toFixed(1)} C${(x1 + dx * a).toFixed(1)} ${y1.toFixed(1)}, ${(x1 + dx * b).toFixed(1)} ${y2.toFixed(1)}, ${x2.toFixed(1)} ${y2.toFixed(1)}`;
};

/** Ringen: åtte fagområde rundt eit felles datagrunnlag. Teiknar seg sjølv éin gong når han blir montert – og er så i ro.
    (Enklare etter tilbakemelding: ingen bølgjer, ingen roterande ring, ingen prikkar som flyt.) */
const Orbit = forwardRef(function Orbit({ animate, testId }, ref) {
  const ringLen = 2 * Math.PI * R;
  const spokeLen = R - CORE_R - 4;
  const pct = (v) => `${((v / S) * 100).toFixed(2)}%`;
  return (
    <div ref={ref} className="offer-orbit" data-testid={testId} data-animate={animate ? "1" : "0"} role="img" aria-label="Åtte fagområde rundt eit felles datagrunnlag">
      <span className="offer-orbit-halo" style={{ width: pct(2 * (CORE_R + 16)) }} aria-hidden="true" />
      <svg viewBox={`0 0 ${S} ${S}`} className="offer-orbit-svg" aria-hidden="true">
        <circle cx={C} cy={C} r={R} className="offer-orbit-ring" style={{ "--len": ringLen }} transform={`rotate(-90 ${C} ${C})`} />
        {NODES.map((n) => (
          <line key={n.label} x1={n.x} y1={n.y} x2={C + (CORE_R + 4) * n.cos} y2={C + (CORE_R + 4) * n.sin} className="offer-orbit-spoke" style={{ "--len": spokeLen, "--i": n.i }} />
        ))}
        {NODES.map((n) => (
          <g key={n.label} className="offer-orbit-node" style={{ "--i": n.i, "--ox": `${(n.cos * 28).toFixed(1)}px`, "--oy": `${(n.sin * 28).toFixed(1)}px` }}>
            <circle cx={n.x} cy={n.y} r={5} className="offer-orbit-anchor" />
          </g>
        ))}
      </svg>
      {NODES.map((n) => (
        <span key={n.label} className="offer-orbit-label" data-side={n.side} style={{ left: pct(n.x), top: pct(n.y), "--i": n.i }}>{n.label}</span>
      ))}
      <div className="offer-orbit-disc" aria-hidden="true"><Layers className="h-6 w-6" /></div>
      <div className="offer-orbit-core-text"><b>Felles datagrunnlag</b></div>
    </div>
  );
});

export const DataFlow = ({ testId = "offer-flow" }) => {
  const id = useId().replace(/:/g, "");
  const root = useRef(null);
  const coreRef = useRef(null);
  const kiRef = useRef(null);
  const sources = useRef([]);
  const wide = useMedia("(min-width: 1280px)");
  const reduce = useMedia("(prefers-reduced-motion: reduce)");
  const near = useVisible(root, "35%");
  const center = useVisible(root, "-18%"); /* ein del av illustrasjonen er inne i den midtre delen av biletet */
  const far = useVisible(root, "900px");
  /* Inngangen: «pre» (klar, elementa ventar usynlege – berre med JS og utan redusert rørsle) → «on» (koreografien spelar).
     Startar når illustrasjonen faktisk er i biletet; tryggingsnett: nær i 1,4 s → start uansett. Langt unna → tilbake til «pre»,
     så ho spelar på nytt når ein kjem att. Utan IntersectionObserver er alt «on» med ein gong. */
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (center) setOn(true);
  }, [center]);
  useEffect(() => {
    if (!near) return undefined;
    const t = setTimeout(() => setOn(true), 1400);
    return () => clearTimeout(t);
  }, [near]);
  useEffect(() => {
    if (!far) setOn(false);
  }, [far]);
  const [geo, setGeo] = useState({ w: 0, h: 0, paths: [] });

  useLayoutEffect(() => {
    if (!wide || !on) {
      setGeo({ w: 0, h: 0, paths: [] });
      return undefined;
    }
    const el = root.current;
    if (!el) return undefined;
    const measure = () => {
      const cb = el.getBoundingClientRect();
      const core = coreRef.current?.getBoundingClientRect();
      const ki = kiRef.current?.getBoundingClientRect();
      if (!core || !ki) return;
      const inX = core.left - cb.left + core.width * PORT_IN.x, inY = core.top - cb.top + core.height * PORT_IN.y;
      const outX = core.left - cb.left + core.width * PORT_OUT.x, outY = core.top - cb.top + core.height * PORT_OUT.y;
      const paths = sources.current.filter(Boolean).map((s, k) => {
        const r = s.getBoundingClientRect();
        return { d: curve(r.right - cb.left, r.top + r.height / 2 - cb.top, inX, inY, 0.28, 0.5), kind: "in", k };
      });
      /* ut frå ringen: éi grein til kvart av dei tre KI-punkta */
      const items = ki ? Array.from(kiRef.current.querySelectorAll("li")) : [];
      const targets = items.length ? items.map((li) => li.getBoundingClientRect()) : [ki];
      targets.forEach((t, k) => {
        const ex = t.left - cb.left, ey = t.top + t.height / 2 - cb.top;
        paths.push({ d: curve(outX, outY, ex, ey, 0.45, 0.7), kind: "out", end: [ex, ey], k });
      });
      setGeo({ w: cb.width, h: cb.height, paths });
    };
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener("resize", measure);
    /* fontane kan kome etter første måling */
    document.fonts?.ready?.then(measure).catch(() => {});
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [wide, on]);

  return (
    <div ref={root} className="offer-flow" data-state={on ? "on" : "pre"} data-testid={testId} data-beams={geo.paths.length ? "1" : "0"}>
      {wide && geo.paths.length > 0 && (
        <svg className="offer-flow-beams" width={geo.w} height={geo.h} viewBox={`0 0 ${geo.w} ${geo.h}`} aria-hidden="true" data-testid={`${testId}-beams`}>
          {geo.paths.map((p, i) => (
            <path key={i} id={`${id}-p${i}`} d={p.d} pathLength="1" className={`offer-flow-beam offer-flow-beam-${p.kind}`} style={{ "--k": p.k }} />
          ))}
          {geo.paths.filter((p) => p.end).map((p, i) => (
            <circle key={`e${i}`} cx={p.end[0]} cy={p.end[1]} r={3.5} className="offer-flow-end" style={{ "--k": p.k }} />
          ))}
        </svg>
      )}

      {/* 01 – der informasjonen finst i dag */}
      <div className="offer-flow-col offer-flow-sources">
        <div className="offer-flow-label" style={{ "--c": 950 }}><span className="offer-num offer-tabular">01</span><span className="offer-flow-label-text">Der informasjonen finst i dag</span></div>
        <div className="offer-flow-body">
          <ul className="offer-flow-list">
            {SOURCES.map((s, i) => (
              <li key={s.label} ref={(el) => { sources.current[i] = el; }} className="offer-flow-pill" style={{ "--i": i }} data-testid={`${testId}-source-${i}`}>
                <span className="offer-flow-pill-icon"><s.icon className="h-4 w-4" /></span>
                <b className="offer-flow-pill-name">{s.label}</b>
                <span className="offer-flow-pill-sub">{s.sub}</span>
              </li>
            ))}
          </ul>
          <p className="offer-flow-foot">Illustrasjon. Det faktiske systemlandskapet kartlegg vi i veke 1.</p>
        </div>
      </div>

      <div className="offer-flow-join" aria-hidden="true"><i /></div>

      {/* 02 – relevante data kopla saman (ringen frå presentasjonen, tilpassa) */}
      <div className="offer-flow-col offer-flow-core-col">
        <div className="offer-flow-label" style={{ "--c": 0 }}><span className="offer-num offer-tabular">02</span><span className="offer-flow-label-text">Relevante data kopla saman</span></div>
        <div className="offer-flow-body offer-flow-core" data-testid={`${testId}-core`}>
          {on ? <Orbit ref={coreRef} animate={!reduce} testId={`${testId}-orbit`} /> : <div ref={coreRef} className="offer-orbit" aria-hidden="true" />}
          <p className="offer-orbit-print" aria-hidden="true">Felles datagrunnlag</p>
          <ul className="offer-orbit-chips" aria-hidden="true">{DOMAINS.map((d) => <li key={d}>{d}</li>)}</ul>
          <p className="offer-flow-core-text" data-testid={`${testId}-core-text`}>
            <b>Same informasjon – uansett kvar ho vart registrert.</b> Gjennom dagens system, integrasjonar eller noko nytt. Kva som er rett for Stord Innkvartering, er noko av det dei fire vekene skal svare på.
          </p>
        </div>
      </div>

      <div className="offer-flow-join" aria-hidden="true"><i /></div>

      {/* 03 – kva KI då kan gjere, og kven som avgjer */}
      <div className="offer-flow-col offer-flow-ki-col">
        <div className="offer-flow-label" style={{ "--c": 1850 }}><span className="offer-num offer-tabular">03</span><span className="offer-flow-label-text">Kva KI då kan gjere</span></div>
        <div className="offer-flow-body">
          <ol ref={kiRef} className="offer-flow-ki" data-testid={`${testId}-ki`}>
            {KI.map((k, i) => (
              <li key={k.v} style={{ "--i": i }} data-testid={`${testId}-ki-${i}`}>
                <b className="offer-flow-ki-title"><em>KI</em> kan {k.v}</b>
                <span className="offer-flow-ki-text">{k.t}</span>
              </li>
            ))}
          </ol>
          <div className="offer-flow-down" aria-hidden="true"><i /><span className="offer-flow-down-text">forslag</span></div>
          <div className="offer-flow-people" data-testid={`${testId}-people`}>
            <span className="offer-flow-people-icon"><UserCheck className="h-[18px] w-[18px]" /></span>
            <div>
              <b className="offer-flow-people-title">Folk avgjer</b>
              <span className="offer-flow-people-text">Dei som kjenner arbeidet, stadfestar, justerer eller avviser. Avgjerdene blir logga – og blir sjølve ein del av datagrunnlaget.</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------------- Utgangspunktet: scena «frå spreidde initiativ til felles retning» ----------------
   Ei fast («sticky») mørk scene som følgjer rullinga i tre takter – framdrifta er ein rein funksjon av rulleposisjonen,
   så ingenting kan bli hengande halvvegs, og ein kan rulle fram og tilbake:
     01  Startar ikkje frå null – modulane (verktøy og initiativ som alt er i gang) blir tende éin og éin, kvar på sin stad.
     02  Kvar for seg – dei driv roleg frå kvarandre og dimmar; dei tre parallelle hendingane kjem som tekst.
     03  Felles retning – éi presis linje teiknar seg gjennom scena, og modulane legg seg på linja.
   Alt er HTML (alltid synleg for lesarar/print). Berre transform/opacity rører seg; seks modular, tre tekstar, tre
   framdriftsstrekar og éi linje – skrive rett til DOM i rAF, ingen React-rendring per bilete. Standardtilstand (utan JS,
   ved redusert rørsle, på print): sluttilstanden – modulane på linje under dei tre tekstane, statisk. */
const SCENE_B1 = 0.34, SCENE_B2 = 0.67;
const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
const seg = (p, a, b) => clamp01((p - a) / (b - a));
const smooth = (t) => t * t * (3 - 2 * t);
const easeIO = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const lerp = (a, b, t) => a + (b - a) * t;

/* a/b: posisjonar (i % av scena) i takt 01 og 02 (b har òg ei lita dreiing), am/bm: det same for den kompakte scena. */
const MODULES = [
  { icon: BookOpen, t: "Sana", s: "Kunnskap og opplæring", a: [14, 22], b: [8, 16, -4], am: [22, 14], bm: [16, 10, -4] },
  { icon: PenTool, t: "Miro", s: "Arbeidsflater og prosessar", a: [36, 52], b: [32, 54, 2.5], am: [74, 20], bm: [82, 16, 3] },
  { icon: Database, t: "Dagens ERP", s: "Ordre, prosjekt og økonomi", a: [58, 22], b: [60, 14, -2], am: [30, 48], bm: [34, 50, -2] },
  { icon: FlaskConical, t: "KI-initiativ", s: "Utprøving i fleire delar av organisasjonen", a: [82, 46], b: [91, 44, 4], am: [78, 52], bm: [84, 54, 4] },
  { icon: Workflow, t: "Nye arbeidsmåtar", s: "Testa lokalt i avdelingane", a: [24, 80], b: [18, 88, -3], am: [24, 84], bm: [18, 90, -3] },
  { icon: Users, t: "Intern drivkraft", s: "Vilje til å finne betre måtar å jobbe på", a: [66, 78], b: [74, 88, 3.5], am: [72, 80], bm: [76, 86, 3.5] },
];
/* sluttposisjonar: éi rad langs ei linje som stig svakt (brei scene) / to rader (kompakt) */
const SLOTS_WIDE = MODULES.map((_, i) => [9 + 16.4 * i, 58 - 3.2 * i]);
const SLOTS_COMPACT = MODULES.map((_, i) => [20 + 30 * (i % 3), i < 3 ? 30 : 72]);
const LINE_WIDE = { y0: 58 + 3.2 * (9 / 16.4), y1: 58 - 3.2 * (91 / 16.4) };
const STEPS = ["I gang", "Kvar for seg", "Felles retning"];

export const InitiativeScene = ({ beats, testId = "offer-scene" }) => {
  const trackRef = useRef(null), stageRef = useRef(null), areaRef = useRef(null);
  const mods = useRef([]), caps = useRef([]), bars = useRef([]), steps = useRef([]), lines = useRef([]);
  const headRef = useRef(null);
  const dims = useRef({ top: 72, W: 1, H: 1, theta: 0, k: 1, dy: 0 });
  const reduce = useMedia("(prefers-reduced-motion: reduce)");
  const compact = useMedia("(max-width: 1023px)");
  const near = useVisible(trackRef, "30%");
  const motion = !reduce;
  const slots = compact ? SLOTS_COMPACT : SLOTS_WIDE;
  const compactRef = useRef(compact);
  compactRef.current = compact;

  const measure = () => {
    const stage = stageRef.current, area = areaRef.current;
    if (!stage || !area) return;
    const W = Math.max(1, area.clientWidth), H = Math.max(1, area.clientHeight);
    const top = parseFloat(window.getComputedStyle(stage).top) || 0;
    const dy = compactRef.current ? 0 : ((LINE_WIDE.y1 - LINE_WIDE.y0) / 100) * H;
    dims.current = { top, W, H, dy, theta: (Math.atan2(dy, W) * 180) / Math.PI, k: Math.hypot(W, dy) / W };
  };

  const render = (p) => {
    const stage = stageRef.current;
    if (!stage) return;
    const { W, H, theta, k, dy } = dims.current;
    const isCompact = compactRef.current;
    const t1 = seg(p, 0, SCENE_B1), t2 = seg(p, SCENE_B1, SCENE_B2), t3 = seg(p, SCENE_B2, 1);
    const beat = p < SCENE_B1 ? 0 : p < SCENE_B2 ? 1 : 2;
    stage.dataset.beat = String(beat + 1);
    stage.style.setProperty("--glow", (0.2 + 0.32 * easeIO(t3)).toFixed(3));
    const drift = easeIO(t2), dim = lerp(1, 0.42, smooth(t2));
    MODULES.forEach((m, i) => {
      const el = mods.current[i];
      if (!el) return;
      const A = isCompact ? m.am : m.a, B = isCompact ? m.bm : m.b, C = (isCompact ? SLOTS_COMPACT : SLOTS_WIDE)[i];
      const on = smooth(seg(t1, 0.06 + i * 0.12, 0.2 + i * 0.12));
      const gather = easeIO(seg(t3, 0.04 + i * 0.06, 0.58 + i * 0.06));
      const x = lerp(lerp(A[0], B[0], drift), C[0], gather);
      const y = lerp(lerp(A[1], B[1], drift), C[1], gather);
      const rot = B[2] * drift * (1 - gather);
      const lit = on * lerp(dim, 1, gather);
      el.style.transform = `translate(${(((x - C[0]) / 100) * W).toFixed(1)}px, ${(((y - C[1]) / 100) * H).toFixed(1)}px) rotate(${rot.toFixed(2)}deg) scale(${lerp(0.94, 1, on).toFixed(3)})`;
      el.style.setProperty("--lit", lit.toFixed(3));
    });
    /* tekstane: kryssar ved taktskifta */
    const c0 = 1 - seg(p, SCENE_B1 - 0.05, SCENE_B1);
    const c1in = seg(p, SCENE_B1, SCENE_B1 + 0.05), c1out = seg(p, SCENE_B2 - 0.05, SCENE_B2);
    const c2 = seg(p, SCENE_B2, SCENE_B2 + 0.05);
    const setCap = (i, o, y) => {
      const el = caps.current[i];
      if (!el) return;
      el.style.opacity = o.toFixed(3);
      el.style.transform = `translateY(${y.toFixed(1)}px)`;
      el.style.visibility = o > 0.001 ? "visible" : "hidden";
    };
    setCap(0, c0, -12 * (1 - c0));
    setCap(1, c1in * (1 - c1out), 14 * (1 - c1in) - 12 * c1out);
    setCap(2, c2, 14 * (1 - c2));
    /* framdrift */
    [t1, t2, t3].forEach((t, i) => {
      if (bars.current[i]) bars.current[i].style.transform = `scaleX(${t.toFixed(4)})`;
      if (steps.current[i]) steps.current[i].dataset.active = beat === i ? "1" : "0";
    });
    /* linja (ei brei: skrå; kompakt: to vassrette) + lysande hovud som teiknar henne */
    const lineT = easeIO(seg(t3, 0, 0.72));
    if (isCompact) {
      if (lines.current[0]) lines.current[0].style.transform = `scaleX(${easeIO(seg(t3, 0, 0.6)).toFixed(4)})`;
      if (lines.current[1]) lines.current[1].style.transform = `scaleX(${easeIO(seg(t3, 0.12, 0.72)).toFixed(4)})`;
      if (headRef.current) headRef.current.style.opacity = "0";
    } else {
      if (lines.current[0]) lines.current[0].style.transform = `rotate(${theta.toFixed(3)}deg) scaleX(${(k * lineT).toFixed(4)})`;
      if (headRef.current) {
        headRef.current.style.opacity = lineT > 0.005 && lineT < 0.995 ? "1" : "0";
        headRef.current.style.transform = `translate(${(W * lineT).toFixed(1)}px, ${(dy * lineT).toFixed(1)}px)`;
      }
    }
  };

  const update = () => {
    const track = trackRef.current, stage = stageRef.current;
    if (!track || !stage) return;
    const r = track.getBoundingClientRect();
    const total = Math.max(1, r.height - stage.offsetHeight);
    render(clamp01((dims.current.top - r.top) / total));
  };

  /* første teikning før måling (ingen blink), og rydding når rørsle er av */
  useLayoutEffect(() => {
    if (!motion) {
      [...mods.current, ...caps.current, ...bars.current, ...lines.current, headRef.current, stageRef.current].forEach((el) => {
        if (el) el.style.cssText = "";
      });
      if (stageRef.current) delete stageRef.current.dataset.beat;
      return;
    }
    measure();
    update();
  }, [motion, compact]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!motion) return undefined;
    let raf = 0;
    const tick = () => {
      raf = 0;
      update();
    };
    const onScroll = () => {
      if (!raf) raf = window.requestAnimationFrame(tick);
    };
    const onResize = () => {
      measure();
      onScroll();
    };
    const ro = typeof ResizeObserver !== "undefined" && areaRef.current ? new ResizeObserver(onResize) : null;
    ro?.observe(areaRef.current);
    window.addEventListener("resize", onResize);
    /* rullelyttaren berre medan scena er i nærleiken; elles éi oppdatering så kviletilstanden er rett (0 eller 1) */
    if (near) window.addEventListener("scroll", onScroll, { passive: true });
    onResize();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      ro?.disconnect();
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [motion, compact, near]); // eslint-disable-line react-hooks/exhaustive-deps

  const lineTops = compact ? [30, 72] : [LINE_WIDE.y0];

  return (
    <div ref={trackRef} className="offer-scene" data-testid={testId} data-motion={motion ? "1" : "0"}>
      <div ref={stageRef} className="offer-scene-stage" data-testid={`${testId}-stage`}>
        <div className="offer-scene-caps">
          {beats.map((b, i) => (
            <div key={b.display} ref={(el) => (caps.current[i] = el)} className="offer-scene-cap" data-testid={`${testId}-cap-${i}`}>
              <p className="offer-scene-display">{b.display}</p>
              <div className="offer-scene-cap-body"><p className={`offer-scene-sub ${i === 2 ? "offer-scene-sub-strong" : ""}`} data-testid={`${testId}-sub-${i}`}>{b.sub}</p></div>
            </div>
          ))}
        </div>

        <div ref={areaRef} className="offer-scene-area" aria-hidden="false">
          {lineTops.map((top, i) => <span key={top} ref={(el) => (lines.current[i] = el)} className="offer-scene-line" style={{ top: `${top}%` }} aria-hidden="true" />)}
          {!compact && <span ref={headRef} className="offer-scene-head" style={{ top: `${LINE_WIDE.y0}%` }} aria-hidden="true" />}
          <ul className="offer-scene-mods" aria-label="Verktøy og initiativ som allereie er i gang hos Stord Innkvartering">
            {MODULES.map((m, i) => (
              <li key={m.t} ref={(el) => (mods.current[i] = el)} className="offer-mod" style={motion ? { left: `${slots[i][0]}%`, top: `${slots[i][1]}%` } : undefined} data-testid={`${testId}-mod-${i}`}>
                <span className="offer-mod-glow" aria-hidden="true" />
                <span className="offer-mod-led" aria-hidden="true" />
                <span className="offer-mod-icon"><m.icon className="h-4 w-4" /></span>
                <b className="offer-mod-title">{m.t}</b>
                <span className="offer-mod-sub">{m.s}</span>
              </li>
            ))}
          </ul>
        </div>

        <ol className="offer-scene-steps" aria-hidden="true" data-testid={`${testId}-steps`}>
          {STEPS.map((label, i) => (
            <li key={label} ref={(el) => (steps.current[i] = el)} className="offer-scene-step" data-active={i === 2 ? "1" : "0"}>
              <span className="offer-scene-step-bar"><i ref={(el) => (bars.current[i] = el)} /></span>
              <span className="offer-scene-step-label"><span className="offer-num offer-tabular">0{i + 1}</span><span>{label}</span></span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
};

/* ---------------- Avslutning: modulane frå heroen, éin gong, roleg ----------------
   Same Lottie som i heroen (same omfarging), men utan løkke og i roleg tempo: byggjer seg opp éin gong når avslutninga
   er godt inne i biletet, og blir ståande ferdig bygd. `onDone` blir kalla når modulane står (eller straks ved redusert
   rørsle / om animasjonen ikkje kan lastast), så teksten under kan kome idet bygget er ferdig. */
export const CodaModules = ({ data, onDone, speed = 0.6, testId = "offer-coda-animation" }) => {
  const wrapRef = useRef(null);
  const handle = useRef(null);
  const [ready, setReady] = useState(false);
  const [inView, setInView] = useState(false);
  const reduce = useMedia("(prefers-reduced-motion: reduce)");
  const played = useRef(false);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return undefined;
    }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setInView(true);
        io.disconnect();
      }
    }, { threshold: 0.45 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!ready || !handle.current) return;
    if (reduce) {
      handle.current.seek({ percent: 1 });
      doneRef.current?.();
      return;
    }
    if (inView && !played.current) {
      played.current = true;
      handle.current.play();
    }
  }, [ready, inView, reduce]);

  /* utan data (lasting feila) skal teksten likevel kome */
  useEffect(() => {
    if (data || !inView) return undefined;
    const t = window.setTimeout(() => doneRef.current?.(), 1200);
    return () => window.clearTimeout(t);
  }, [data, inView]);

  return (
    <div ref={wrapRef} className="offer-coda-art" data-testid={`${testId}-wrap`} data-ready={ready ? "1" : "0"}>
      <div className="offer-coda-glow" aria-hidden="true" />
      {data ? (
        <LottieSvg src={data} lottieRef={handle} autoplay={false} loop={false} speed={speed}
          subscriptions={{ ready: () => setReady(true), complete: () => doneRef.current?.(), error: () => doneRef.current?.() }}
          rendererSettings={{ preserveAspectRatio: "xMidYMid meet", progressiveLoad: true }}
          className="offer-coda-lottie aspect-[16/9]" aria-label="Modulbygg som blir bygd opp" role="img" data-testid={testId} />
      ) : (
        <div className="offer-coda-lottie aspect-[16/9]" aria-hidden="true" />
      )}
    </div>
  );
};
